// Thin fetch wrapper for the sphinx REST API (/api/sphinx, see
// server/routes/sphinx/*). JSON in/out, camelCase keys. Auth is the Yarn
// sign-in session cookie (sent by the browser; `lib/sphinx-auth.ts`), or the
// optional SPHINX_ACCESS_KEY service path when a key is stored
// (`lib/sphinx-access-key.ts`, sent as `x-sphinx-access-key`). A 401
// `unauthenticated` flips the app to the sign-in screen (`requestSignIn`) and
// throws. Non-2xx responses throw a `SphinxApiError` carrying the status and
// the server's `{ error, issues? }` body so callers can render it.
import type { ServerFormData } from "@/shared/types";
import type {
  RunStatus,
  SphinxModel,
  TargetId,
  YarnEnv,
  YarnOrg,
} from "@/shared/sphinx";
import {
  accessKeyHeaders,
  getSphinxAccessKey,
  SPHINX_ACCESS_KEY_HEADER,
} from "./sphinx-access-key";
import { isUnauthenticatedResponse, requestSignIn } from "./sphinx-auth";

export { SPHINX_ACCESS_KEY_HEADER };

export const SPHINX_API_BASE = "/api/sphinx";

export class SphinxApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly issues?: unknown[];

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "SphinxApiError";
    this.status = status;
    this.body = body;
    const issues = (body as { issues?: unknown } | null)?.issues;
    if (Array.isArray(issues)) this.issues = issues;
  }
}

// ---- wire types (rowToJson output) -----------------------------------------

export type SphinxServer = {
  id: string;
  name: string;
  config: ServerFormData;
  createdAt: string;
  updatedAt: string;
};

export type SphinxSuiteSummary = {
  id: string;
  name: string;
  updatedAt: string;
  caseCount: number;
};

export type SphinxCase = {
  id: string;
  suiteId: string;
  ord: number;
  json: unknown;
  createdAt: string;
  updatedAt: string;
};

export type SphinxSuite = {
  id: string;
  name: string;
  yaml: string;
  createdAt: string;
  updatedAt: string;
  cases?: SphinxCase[];
};

export type SphinxIteration = {
  id: string;
  runId: string;
  caseId: string;
  target: TargetId;
  /**
   * The model this iteration ran. A run expands to case × client × MODEL, so
   * one client can appear several times in a run — anything keying on the
   * client alone collides (migration 012).
   */
  model: SphinxModel | null;
  /** Org this iteration ran against (case-pinned, migration 019); null = the run's org. */
  yarnOrg?: string | null;
  status: RunStatus;
  startedAt: string | null;
  finishedAt: string | null;
  clientTranscript: unknown | null;
  /** Why the iteration failed ("orphaned: runner restarted…", "agent stalled: …"); null unless failed. */
  error?: string | null;
  serverTruth: unknown | null;
  grade: unknown | null;
  artifactPrefix: string | null;
  /** Joined from the iteration's job on GET /runs/:id; absent elsewhere. */
  jobState?: string | null;
  runnerName?: string | null;
  /** The frozen spec's case title (GET /runs/:id/iterations) — survives a
   * suite re-seed deleting/re-creating the case rows under new ids. */
  caseTitle?: string | null;
};

export type SphinxRunSummary = { total: number; passed: number; failed: number };

export type SphinxRun = {
  id: string;
  suiteId: string;
  targets: TargetId[];
  yarnEnv: YarnEnv;
  yarnOrg: YarnOrg;
  status: RunStatus;
  note: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** Set by the queue when the run completes; null/absent before that. */
  summary?: SphinxRunSummary | null;
  iterations?: SphinxIteration[];
};

export type SphinxTarget = {
  id: TargetId;
  enabled: boolean;
  model: SphinxModel | null;
  /**
   * Every model this client runs (`targets.models`, migration 012): a run
   * expands to case × client × model. Empty = fall back to `model`, then to
   * the provider default.
   */
  models?: SphinxModel[];
  serverIds: string[];
  config: Record<string, unknown>;
};

export type SphinxChatSession = {
  id: string;
  title: string | null;
  json?: unknown;
  /** Write generation — bumped by every persisted turn. */
  version?: number;
  /** Playground client lineup ("direct" + target ids) — migration 015. */
  lineup?: string[] | null;
  createdAt: string;
  updatedAt: string;
};

export type SphinxEvent = {
  id: number;
  runId: string;
  iterationId: string | null;
  seq: number;
  ts: string;
  json: unknown;
};

// ---- transport ---------------------------------------------------------------

export async function sphinxFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {}
): Promise<T> {
  const headers: Record<string, string> = { ...accessKeyHeaders() };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  const res = await fetch(`${SPHINX_API_BASE}${path}`, {
    method: init.method ?? "GET",
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    signal: init.signal,
    credentials: "same-origin",
  });
  // No session (and no valid access key): show the sign-in screen. The call
  // still throws below so callers see the failure rather than a hang.
  if (await isUnauthenticatedResponse(res)) requestSignIn();
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const message =
      (data as { error?: unknown } | null)?.error?.toString() ??
      `sphinx API ${init.method ?? "GET"} ${path} failed (${res.status})`;
    throw new SphinxApiError(res.status, message, data);
  }
  return data as T;
}

const q = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
};

// ---- endpoints ---------------------------------------------------------------

export const sphinxApi = {
  healthDb: () =>
    sphinxFetch<{ ok: boolean; migrated: number; error?: string; bootId?: string }>("/health-db"),

  servers: {
    list: () => sphinxFetch<SphinxServer[]>("/servers"),
    create: (body: { name: string; config: Record<string, unknown> }) =>
      sphinxFetch<SphinxServer>("/servers", { method: "POST", body }),
    patch: (id: string, body: { name?: string; config?: Record<string, unknown> }) =>
      sphinxFetch<SphinxServer>(`/servers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    delete: (id: string) =>
      sphinxFetch<{ ok: true }>(`/servers/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },

  suites: {
    list: () => sphinxFetch<SphinxSuiteSummary[]>("/eval-suites"),
    create: (body: { name: string; yaml?: string }) =>
      sphinxFetch<SphinxSuite>("/eval-suites", { method: "POST", body }),
    get: (id: string) =>
      sphinxFetch<SphinxSuite & { cases: SphinxCase[] }>(
        `/eval-suites/${encodeURIComponent(id)}`
      ),
    patch: (id: string, body: { name?: string; yaml?: string }) =>
      sphinxFetch<SphinxSuite>(`/eval-suites/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    delete: (id: string) =>
      sphinxFetch<{ ok: true }>(`/eval-suites/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    duplicate: (id: string) =>
      sphinxFetch<SphinxSuite>(
        `/eval-suites/${encodeURIComponent(id)}/duplicate`,
        { method: "POST", body: {} }
      ),
  },

  cases: {
    list: (suiteId: string) =>
      sphinxFetch<SphinxCase[]>(
        `/eval-suites/${encodeURIComponent(suiteId)}/cases`
      ),
    create: (suiteId: string, json: unknown) =>
      sphinxFetch<SphinxCase>(
        `/eval-suites/${encodeURIComponent(suiteId)}/cases`,
        { method: "POST", body: { json } }
      ),
    patch: (id: string, body: { json?: unknown; ord?: number }) =>
      sphinxFetch<SphinxCase>(`/cases/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    delete: (id: string) =>
      sphinxFetch<{ ok: true }>(`/cases/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },

  runs: {
    list: (suiteId?: string) => sphinxFetch<SphinxRun[]>(`/runs${q({ suiteId })}`),
    /** `caseIds` restricts the run to those cases (per-case quick run);
     *  omitted = every case in the suite. */
    create: (
      suiteId: string,
      body: {
        targets: TargetId[];
        yarnEnv: YarnEnv;
        yarnOrg: YarnOrg;
        note?: string;
        caseIds?: string[];
      }
    ) =>
      sphinxFetch<SphinxRun & { iterations: SphinxIteration[] }>(
        `/eval-suites/${encodeURIComponent(suiteId)}/runs`,
        { method: "POST", body }
      ),
    get: (id: string) =>
      sphinxFetch<SphinxRun & { iterations: SphinxIteration[] }>(
        `/runs/${encodeURIComponent(id)}`
      ),
    cancel: (id: string) =>
      sphinxFetch<SphinxRun>(`/runs/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
        body: {},
      }),
    delete: (id: string) =>
      sphinxFetch<{ ok: true }>(`/runs/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    iterations: (id: string) =>
      sphinxFetch<SphinxIteration[]>(
        `/runs/${encodeURIComponent(id)}/iterations`
      ),
    events: (id: string, after = 0) =>
      sphinxFetch<SphinxEvent[]>(
        `/runs/${encodeURIComponent(id)}/events${q({ after })}`
      ),
    /** SSE tail (`event:` = SphinxEvent.type, `id:` = event row id). The
     *  session cookie rides along on same-origin EventSource; EventSource
     *  can't set headers, so a stored access key goes as a query. */
    streamUrl: (id: string, after = 0) =>
      `${SPHINX_API_BASE}/runs/${encodeURIComponent(id)}/stream${q({
        after,
        access_key: getSphinxAccessKey() ?? undefined,
      })}`,
    appendEvent: (id: string, body: { iterationId?: string; json: unknown }) =>
      sphinxFetch<SphinxEvent>(`/runs/${encodeURIComponent(id)}/events`, {
        method: "POST",
        body,
      }),
  },

  playground: {
    /** POST /playground/runs — dispatch one chat message to real clients through the queue. */
    createRun: (body: {
      prompt: string;
      targets: TargetId[];
      /** Explicit connector override; omitted = each client's own attached set. */
      serverIds?: string[];
      yarnEnv?: YarnEnv;
      yarnOrg?: YarnOrg;
      /** Inline per-client model picks; the route drops family mismatches. */
      models?: Partial<Record<TargetId, SphinxModel>>;
      /** The route upserts this session row and records the lineup. */
      chatSessionId?: string;
      /** Full Playground lineup for this send: "direct" and/or target ids. */
      lineup?: string[];
    }) =>
      sphinxFetch<{
        runId: string;
        suiteId: string;
        caseId: string;
        iterations: { iterationId: string; target: TargetId }[];
      }>("/playground/runs", { method: "POST", body }),
    /** GET /playground/runs?chatSessionId= — the chat's dispatches, oldest first. */
    listRunsForChat: (chatSessionId: string) =>
      sphinxFetch<
        {
          runId: string;
          suiteId: string;
          targets: TargetId[];
          prompt: string;
          createdAt: string;
        }[]
      >(
        `/playground/runs?chatSessionId=${encodeURIComponent(chatSessionId)}`
      ),
  },

  iterations: {
    get: (id: string) =>
      sphinxFetch<SphinxIteration>(`/iterations/${encodeURIComponent(id)}`),
    patch: (
      id: string,
      body: Partial<
        Pick<
          SphinxIteration,
          | "status"
          | "startedAt"
          | "finishedAt"
          | "clientTranscript"
          | "serverTruth"
          | "grade"
          | "artifactPrefix"
        >
      >
    ) =>
      sphinxFetch<SphinxIteration>(`/iterations/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
  },

  targets: {
    list: () => sphinxFetch<SphinxTarget[]>("/targets"),
    patch: (
      id: TargetId | string,
      body: {
        enabled?: boolean;
        /** Legacy single-model column; written as the first entry of `models`. */
        model?: SphinxModel | null;
        /** Every model this client runs (`targets.models`, migration 012). */
        models?: SphinxModel[];
        serverIds?: string[];
      }
    ) =>
      sphinxFetch<SphinxTarget>(`/targets/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
  },

  chatSessions: {
    list: () => sphinxFetch<SphinxChatSession[]>("/chat-sessions"),
    create: (body: { title?: string | null; json: unknown }) =>
      sphinxFetch<SphinxChatSession>("/chat-sessions", { method: "POST", body }),
    get: (id: string) =>
      sphinxFetch<SphinxChatSession & { json: unknown }>(
        `/chat-sessions/${encodeURIComponent(id)}`
      ),
    patch: (
      id: string,
      body: { title?: string | null; json?: unknown; lineup?: string[] | null }
    ) =>
      sphinxFetch<SphinxChatSession>(`/chat-sessions/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    delete: (id: string) =>
      sphinxFetch<{ ok: true }>(`/chat-sessions/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },
};

export type SphinxApi = typeof sphinxApi;
